import { useLayoutEffect, useState, type RefObject } from 'react';

export interface UsePaginationResult {
  /** Current 0-indexed page. */
  page: number;
  /** Standard React state setter — accepts a value or an updater function. */
  setPage: (value: number | ((prev: number) => number)) => void;
  /** Total number of pages the content lays out into. */
  pageCount: number;
  /** Vertical offset (px) the content must be shifted up by to show the current page. */
  offset: number;
}

/**
 * Drives the pagination for `PaginatedReader`'s e-reader/tablet display
 * mode: it slices normally-flowing content into fixed-height "book
 * pages" and reports the scroll offset of each.
 *
 * **Why not CSS multi-column?** The obvious implementation is to set
 * `column-width` to the viewport width and let the browser flow content
 * into columns, one column per page. That approach is a trap: a
 * multi-column *box* only creates as many columns as fit within its own
 * width, and every column past that becomes a CSS "overflow column".
 * Overflow columns are among the least consistently specified and
 * implemented corners of the multicol module (long-open, actively
 * disputed browser bugs), and both their rendering and their
 * contribution to `scrollWidth` vary by engine — which makes any
 * page-count/page-offset math built on them unreliable in exactly the
 * ways that matter here.
 *
 * So this measures pages directly instead, which is fully deterministic
 * and needs no layout tricks at all:
 *
 *  1. The content is left to flow **normally** — one plain column, its
 *     natural height — inside a fixed-height, `overflow: hidden`
 *     viewport. Nothing about the content's layout is mutated, so
 *     there's no measure/apply feedback loop to guard against, and
 *     what's measured is exactly what the reader sees.
 *  2. Its block-level elements are walked to find natural break
 *     points, and greedily grouped into pages: a block that would
 *     cross the bottom edge of the current page starts a new page
 *     instead. This is what keeps paragraphs, headings, list items and
 *     the like from being sliced through the middle — the same job
 *     `break-inside: avoid-column` was doing (badly) in CSS, done here
 *     where the outcome is predictable.
 *  3. Turning a page is then just `translateY(-offset)` with the
 *     offset of that page's first block, which `PaginatedReader`
 *     applies.
 *
 * A block taller than a whole page (a big image, a long code block)
 * can't be kept intact, so it's sliced across as many full-height
 * pages as it needs — better than letting it overflow and hide content.
 */
export function usePagination(
  viewportRef: RefObject<HTMLDivElement | null>,
  contentRef: RefObject<HTMLDivElement | null>,
): UsePaginationResult {
  const [page, setPage] = useState(0);
  const [offsets, setOffsets] = useState<number[]>([0]);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) return;

    const measure = () => {
      const pageHeight = viewport.clientHeight;
      if (pageHeight <= 0) return;

      const contentRect = content.getBoundingClientRect();
      const totalHeight = contentRect.height;

      // Offsets are derived from `getBoundingClientRect()` deltas
      // rather than `offsetTop`, so they're correct regardless of how
      // deeply nested a block is inside the renderers' wrapper
      // elements. Both rects include the same `translateY` that's
      // currently applied to the content, so the difference between
      // them is unaffected by it — no need to reset the transform to
      // take a measurement.
      const blocks = collectBlocks(content, pageHeight, contentRect.top);

      const next = [0];
      let pageTop = 0;

      for (const { top, bottom } of blocks) {
        if (top >= pageTop + pageHeight) {
          // Starts past the bottom of the current page — begin a new
          // page here. (Can happen when a preceding oversized block
          // was sliced, or with large margins between blocks.)
          pageTop = top;
          next.push(pageTop);
        } else if (bottom > pageTop + pageHeight) {
          // Crosses the bottom edge: push the whole block to the next
          // page rather than cutting through it.
          if (top > pageTop) {
            pageTop = top;
            next.push(pageTop);
          }
          // Still too tall to fit even on a page of its own — slice it.
          while (bottom > pageTop + pageHeight) {
            pageTop += pageHeight;
            next.push(pageTop);
          }
        }
      }

      // Drop any page that would start at or past the end of the
      // content (possible after slicing an oversized trailing block),
      // so the reader never ends on a blank page.
      const trimmed = next.filter((offset, i) => i === 0 || offset < totalHeight);

      setOffsets((prev) => (sameOffsets(prev, trimmed) ? prev : trimmed));
    };

    measure();

    // Watch both boxes: the viewport for window/orientation resizes,
    // and the content for anything that changes its natural height
    // after first paint (images finishing loading, fonts swapping in,
    // a different article being rendered).
    const ro = new ResizeObserver(measure);
    ro.observe(viewport);
    ro.observe(content);
    return () => ro.disconnect();
  }, [viewportRef, contentRef]);

  const pageCount = offsets.length;
  const clampedPage = Math.min(page, pageCount - 1);

  return {
    page: clampedPage,
    setPage,
    pageCount,
    offset: offsets[clampedPage] ?? 0,
  };
}

interface MeasuredBlock {
  top: number;
  bottom: number;
}

/**
 * Collects the content's break-point candidates: the outermost
 * elements that already fit within a single page.
 *
 * Descending only into elements too tall to fit is what makes this work
 * across all four renderers without knowing anything about their
 * markup. A wrapper that spans the whole article (each renderer's outer
 * `<div>`, a `<section>`, a long `<ul>`) is transparently stepped
 * through to reach the real blocks inside it, while a self-contained
 * unit that does fit (a paragraph, a heading, a list item, an
 * admonition box) is kept whole and never broken up.
 */
function collectBlocks(root: HTMLElement, pageHeight: number, originTop: number, out: MeasuredBlock[] = []): MeasuredBlock[] {
  for (const child of root.children) {
    if (!(child instanceof HTMLElement)) continue;
    if (child.hidden) continue;

    const rect = child.getBoundingClientRect();
    if (rect.height <= 0) continue;

    if (rect.height > pageHeight && child.children.length > 0) {
      collectBlocks(child, pageHeight, originTop, out);
    } else {
      out.push({ top: rect.top - originTop, bottom: rect.bottom - originTop });
    }
  }
  return out;
}

function sameOffsets(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((value, i) => Math.abs(value - b[i]) < 1);
}
